/**
 * Store Reset Utility
 * Provides centralized store reset functionality for tenant context switching
 */

import { useTenants } from './tenant';
import { useUsers } from './user';
import { useGroups } from './group';
import { useApplications } from './application';
import { useRoles } from './role';
import { usePermissions } from './permission';
import { useIdps } from './idp';
import { useOnboarding } from './onboarding';
import { useLogs } from './log';
import { useAssignments } from './assignment';
import { useAnalytics } from './analytics';

/**
 * Reset all Zustand stores to initial state
 * Called before tenant context switch to prevent stale data
 */
export function resetAllStores(): void {
  try {
    // Get store instances and call reset if available
    const stores = [
      useTenants,
      useUsers,
      useGroups,
      useApplications,
      useRoles,
      usePermissions,
      useIdps,
      useOnboarding,
      useLogs,
      useAssignments,
      useAnalytics,
    ];

    stores.forEach((store) => {
      const state = store.getState() as any;
      if (typeof state.reset === 'function') {
        state.reset();
      } else {
        console.warn(`Store ${store.name || 'unknown'} does not have a reset method`);
      }
    });

    console.log('All stores reset successfully');
  } catch (error) {
    console.error('Error resetting stores:', error);
  }
}

/**
 * Clear localStorage data (except auth token)
 * Optional: use if stores persist data to localStorage
 */
export function clearLocalStorageData(): void {
  const keysToPreserve = ['auth_token', 'theme', 'locale'];
  
  Object.keys(localStorage).forEach((key) => {
    if (!keysToPreserve.includes(key)) {
      localStorage.removeItem(key);
    }
  });
}

/**
 * Perform full tenant context switch
 * 1. Reset all Zustand stores
 * 2. Clear localStorage (optional)
 * 3. Redirect to new tenant context
 */
export function switchTenantContext(tenantSlug: string): void {
  // Reset all stores
  resetAllStores();
  
  // Optional: clear localStorage
  // clearLocalStorageData();
  
  // Hard reload to new tenant context
  // User will re-authenticate in new tenant
  window.location.href = `/t/${tenantSlug}`;
}
